<template>
  <SoContainer>
    <h4 class="mb-4 w-50">Energy Usage</h4>
    <div class="d-flex align-center">
      <SoTooltip
        v-if="!tariffQuoter.isGSPSearch"
        text="
          Generate a quote using each selected meterpoint. Use this
          when a customer has multiple of the same meterpoint type
          or wishes to see a more specific quote. Please insure all
          meterpoints for this address have been selected before proceeding.
        "
      >
        <SoCheckbox
          v-model="tariffQuoter.quoteForMeterpoints"
          label="Quote Per Meterpoint"
          class="mr-2"
      /></SoTooltip>
    </div>
    <SoRow>
      <SoCol>
        <SoAlert
          v-if="!tariffQuoter.selectedFuels.length"
          title="No Fuel Selected"
        />
        <SoWidget v-else>
          <template v-if="!tariffQuoter.quoteForMeterpoints">
            <SoTextField
              v-if="
                tariffQuoter.isElectricitySelected &&
                !tariffQuoter.isTwoRateSelected
              "
              :model-value="tariffQuoter.fuelUsage.electricity"
              label="Electricity"
              suffix="kWh"
              type="number"
              data-testid="tq-elec-usage-input"
              @update:model-value="
                ($event) =>
                  (tariffQuoter.fuelUsage.electricity = Number($event))
              "
            />
            <template
              v-else-if="
                tariffQuoter.isElectricitySelected &&
                tariffQuoter.isTwoRateSelected
              "
            >
              <SoTextField
                v-if="tariffQuoter.isElectricitySelected"
                :model-value="tariffQuoter.fuelUsage.electricityDay"
                label="Electricity Day"
                suffix="kWh"
                type="number"
                data-testid="tq-day-usage-input"
                @update:model-value="
                  ($event) =>
                    (tariffQuoter.fuelUsage.electricityDay = Number($event))
                "
              />
              <SoTextField
                v-if="tariffQuoter.isElectricitySelected"
                :model-value="tariffQuoter.fuelUsage.electricityNight"
                label="Electricity Night"
                suffix="kWh"
                type="number"
                data-testid="tq-night-usage-input"
                @update:model-value="
                  ($event) =>
                    (tariffQuoter.fuelUsage.electricityNight = Number($event))
                "
              />
            </template>
            <SoTextField
              v-if="tariffQuoter.isGasSelected"
              :model-value="tariffQuoter.fuelUsage.gas"
              label="Gas"
              suffix="kWh"
              type="number"
              data-testid="tq-gas-usage-input"
              @update:model-value="
                ($event) => (tariffQuoter.fuelUsage.gas = Number($event))
              "
            />
          </template>
          <template v-else>
            <SoAlert v-if="tariffQuoter.hasExportMeter" class="mb-4">
              One or more MPAN is an EXPORT meter and cannot be quoted using
              this tool.
            </SoAlert>
            <template
              v-for="mpxn in tariffQuoter.meterpointFuelUsage"
              :key="mpxn.id"
            >
              <template
                v-if="mpxn.isElectricity && tariffQuoter.isElectricitySelected"
              >
                <SoTextField
                  v-if="!mpxn.isTwoRate"
                  :model-value="mpxn.usage.electricity"
                  :label="`MPAN - ${mpxn.id}`"
                  suffix="kWh"
                  type="number"
                  data-testid="tq-mpan-usage-input"
                  @update:model-value="
                    ($event) => (mpxn.usage.electricity = Number($event))
                  "
                />
                <template v-else>
                  <SoTextField
                    :model-value="mpxn.usage.electricityDay"
                    :label="`MPAN - ${mpxn.id} (Day)`"
                    suffix="kWh"
                    type="number"
                    data-testid="tq-mpan-day-usage-input"
                    @update:model-value="
                      ($event) => (mpxn.usage.electricityDay = Number($event))
                    "
                  />
                  <SoTextField
                    :model-value="mpxn.usage.electricityNight"
                    :label="`MPAN - ${mpxn.id} (Night)`"
                    suffix="kWh"
                    type="number"
                    data-testid="tq-mpan-night-usage-input"
                    @update:model-value="
                      ($event) => (mpxn.usage.electricityNight = Number($event))
                    "
                  />
                </template>
                <SoCheckbox
                  v-model="mpxn.isTwoRate"
                  label="2-Rate Meter"
                  class="mb-4"
                />
              </template>
              <SoTextField
                v-if="!mpxn.isElectricity && tariffQuoter.isGasSelected"
                :model-value="mpxn.usage.gas"
                :label="`MPRN - ${mpxn.id}`"
                suffix="kWh"
                type="number"
                data-testid="tq-mprn-usage-input"
                @update:model-value="
                  ($event) => (mpxn.usage.gas = Number($event))
                "
              />
            </template>
          </template>
        </SoWidget>
      </SoCol>
      <SoCol>
        <TariffQuoterEstimationOverview />
      </SoCol>
    </SoRow>
  </SoContainer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useTariffQuoter } from "@/stores/tariffQuoter"
  import TariffQuoterEstimationOverview from "../components/TariffQuoterEstimationOverview.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoContainer from "@/components/SoContainer.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoTooltip from "@/components/SoTooltip.vue"

  export default defineComponent({
    components: {
      TariffQuoterEstimationOverview,
      SoWidget,
      SoContainer,
      SoRow,
      SoCol,
      SoTextField,
      SoAlert,
      SoCheckbox,
      SoTooltip,
    },
    data() {
      return {
        tariffQuoter: useTariffQuoter(),
      }
    },
  })
</script>
